/**
 * Birthday verification step. Patient must enter their date of birth
 * before accessing the survey.
 * @agentic members
 */

import { useState, useCallback, useRef } from "react";
import {
  Paper,
  Stack,
  Title,
  Text,
  Button,
  Group,
  Alert,
  NumberInput,
} from "@mantine/core";

// Must match the server's BIRTHDAY_MAX_FAILURES (apps/api surveys/handler.ts):
// keep the client's local counter aligned with the server-enforced lockout so
// the UI never claims "out of attempts" while the server would still accept
// tries, and vice versa. The server 429 lockout remains the source of truth.
const MAX_ATTEMPTS = 5;

type BirthdayVerificationProps = {
  onError: (error: unknown) => void;
  onVerify: (birthDate: string) => Promise<void>;
};

export const BirthdayVerification = ({
  onError,
  onVerify,
}: BirthdayVerificationProps) => {
  const [month, setMonth] = useState<number | string>("");
  const [day, setDay] = useState<number | string>("");
  const [year, setYear] = useState<number | string>("");
  const [attempts, setAttempts] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const dayRef = useRef<HTMLInputElement>(null);
  const yearRef = useRef<HTMLInputElement>(null);

  // Segmented date entry: once a field holds two digits it can take no further
  // input (month tops out at 12, day at 31 — leading zeros are normalized away,
  // so a two-digit entry is a value >= 10), so move focus to the next field to
  // spare the patient a manual tab. `select()` lets an immediate keystroke
  // replace the value if the patient is correcting a pre-filled field.
  const advanceFocus = useCallback((next: HTMLInputElement | null) => {
    if (!next) return;
    next.focus();
    next.select();
  }, []);

  // Center the digits and the field label so the three inputs read as one
  // MM · DD · YYYY date control.
  const dateFieldStyles = {
    input: { textAlign: "center" as const },
    label: { display: "block", textAlign: "center" as const },
  };

  const isComplete =
    typeof month === "number" &&
    typeof day === "number" &&
    typeof year === "number" &&
    month >= 1 &&
    month <= 12 &&
    day >= 1 &&
    day <= 31 &&
    year >= 1900 &&
    year <= new Date().getFullYear();

  const handleSubmit = useCallback(async () => {
    if (
      !isComplete ||
      typeof month !== "number" ||
      typeof day !== "number" ||
      typeof year !== "number"
    )
      return;

    if (attempts >= MAX_ATTEMPTS) {
      onError(
        new Error(
          "Too many failed attempts. Please contact your care team for a new survey link.",
        ),
      );
      return;
    }

    const birthDate = `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;

    setLoading(true);
    setError(null);

    try {
      await onVerify(birthDate);
    } catch (err) {
      const isBirthdayMismatch =
        err instanceof Error &&
        err.message.includes("Date of birth does not match");

      if (!isBirthdayMismatch) {
        setLoading(false);
        onError(err);
        return;
      }

      const newAttempts = attempts + 1;
      setAttempts(newAttempts);

      if (newAttempts >= MAX_ATTEMPTS) {
        onError(
          new Error(
            "Too many failed attempts. Please contact your care team for a new survey link.",
          ),
        );
      } else {
        const remaining = MAX_ATTEMPTS - newAttempts;
        setError(
          `Date of birth does not match our records. ${remaining} attempt${remaining === 1 ? "" : "s"} remaining.`,
        );
      }
      setLoading(false);
    }
  }, [isComplete, month, day, year, attempts, onVerify, onError]);

  return (
    <Paper radius="lg" p="xl" shadow="sm">
      <Stack gap="lg">
        <Stack gap="xs">
          <Title order={2} ta="center" c="#391640">
            Verify Your Identity
          </Title>
          <Text ta="center" style={{ opacity: 0.55, color: "#2a1a2e" }}>
            For your security, please enter the date of birth on file to access
            this survey.
          </Text>
        </Stack>

        {error && (
          <Alert color="red" variant="light">
            {error}
          </Alert>
        )}

        <Group justify="center" gap="sm" align="flex-end">
          <NumberInput
            label="Month"
            placeholder="MM"
            min={1}
            max={12}
            value={month}
            onChange={(value) => {
              setMonth(value);
              // Two digits entered (10–12) → jump to the day field.
              if (typeof value === "number" && value >= 10) {
                advanceFocus(dayRef.current);
              }
            }}
            w={72}
            size="md"
            hideControls
            inputMode="numeric"
            autoFocus
            styles={dateFieldStyles}
            aria-label="Birth month"
          />
          <NumberInput
            ref={dayRef}
            label="Day"
            placeholder="DD"
            min={1}
            max={31}
            value={day}
            onChange={(value) => {
              setDay(value);
              // Two digits entered (10–31) → jump to the year field.
              if (typeof value === "number" && value >= 10) {
                advanceFocus(yearRef.current);
              }
            }}
            w={72}
            size="md"
            hideControls
            inputMode="numeric"
            styles={dateFieldStyles}
            aria-label="Birth day"
          />
          <NumberInput
            ref={yearRef}
            label="Year"
            placeholder="YYYY"
            min={1900}
            max={new Date().getFullYear()}
            value={year}
            onChange={setYear}
            w={96}
            size="md"
            hideControls
            inputMode="numeric"
            styles={dateFieldStyles}
            aria-label="Birth year"
          />
        </Group>

        <Group justify="center">
          <Button
            size="lg"
            onClick={handleSubmit}
            disabled={!isComplete}
            loading={loading}
          >
            Continue to Survey
          </Button>
        </Group>
      </Stack>
    </Paper>
  );
};
